import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Cell
} from 'recharts';
import { 
  Target, TrendingUp, TrendingDown, Users, DollarSign,
  AlertTriangle, RefreshCw, Loader2, CheckCircle2, Clock,
  Building, Gauge, Lightbulb, ChevronRight, ArrowRight,
  Flag, Milestone, Shield, Heart, Award, Rocket, AlertCircle,
  Calendar, MapPin, Briefcase, BarChart3, PieChart, Bell, Trophy, Zap,
  Database, Sparkles
} from 'lucide-react';
import { 
  PageHeader, SectionHeader, ModernCard,
  modernTabsClass, modernTabTriggerClass
} from '@/components/ModernUI';
import {
  MilestoneBadge,
  PhaseProgressBadge,
  AlertCountBadge,
  MilestoneAlertCard,
  showMilestoneToast,
  MILESTONES
} from '@/components/MilestoneAlerts';
import { InsightsExport } from '@/components/InsightsExport';
import { DataHistoryPanel } from '@/components/DataHistory';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase colors
const phaseColors = {
  phase_1: { bg: 'bg-slate-500', text: 'text-slate-600', gradient: 'from-slate-500 to-slate-600' },
  phase_2: { bg: 'bg-blue-500', text: 'text-blue-600', gradient: 'from-blue-500 to-blue-600' },
  phase_3: { bg: 'bg-violet-500', text: 'text-[#00b0ab]', gradient: 'from-violet-500 to-violet-600' },
  phase_4: { bg: 'bg-amber-500', text: 'text-amber-600', gradient: 'from-amber-500 to-amber-600' },
  phase_5: { bg: 'bg-emerald-500', text: 'text-emerald-600', gradient: 'from-emerald-500 to-emerald-600' }
};

// Priority badge colors
const priorityColors = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH: 'bg-amber-100 text-amber-700 border-amber-200',
  MEDIUM: 'bg-blue-100 text-blue-700 border-blue-200',
  STRATEGIC: 'bg-violet-100 text-violet-700 border-violet-200'
};

// Phase Timeline Component with Milestone Badges
const PhaseTimeline = ({ phases, currentPhase, currentAdc }) => {
  return (
    <div className="relative" data-testid="phase-timeline">
      {/* Timeline line */}
      <div className="absolute top-6 left-0 right-0 h-1 bg-slate-200 dark:bg-slate-700" />
      
      <div className="relative flex justify-between">
        {phases.map((phase, idx) => {
          const isComplete = phase.status === 'completed';
          const isCurrent = phase.id === currentPhase;
          const colors = phaseColors[phase.id];
          const progressToPhase = currentAdc ? Math.min(100, (currentAdc / phase.adc_target) * 100) : 0;
          const isApproaching = isCurrent && progressToPhase >= 85 && progressToPhase < 100;
          
          // Determine milestone status for badge
          let milestoneStatus = 'upcoming';
          if (isComplete) milestoneStatus = 'achieved';
          else if (isCurrent && isApproaching) milestoneStatus = 'approaching';
          else if (isCurrent) milestoneStatus = 'in_progress';
          
          return (
            <div key={phase.id} className="flex flex-col items-center" style={{ width: '18%' }}>
              {/* Phase node */}
              <div 
                className={`
                  relative z-10 w-12 h-12 rounded-full flex items-center justify-center
                  transition-all duration-300
                  ${isComplete ? `${colors.bg} text-white` : 
                    isCurrent ? `${colors.bg} text-white ring-4 ring-offset-2 ring-${colors.bg.replace('bg-', '')}` : 
                    'bg-slate-200 dark:bg-slate-700 text-slate-500'}
                  ${isApproaching ? 'animate-pulse' : ''}
                `}
              >
                {isComplete ? (
                  <Trophy className="h-6 w-6" />
                ) : isApproaching ? (
                  <Zap className="h-6 w-6" />
                ) : (
                  <span className="font-bold">{phase.year}</span>
                )}
              </div>
              
              {/* Phase label */}
              <div className="mt-3 text-center">
                <p className={`font-semibold text-sm ${isCurrent ? colors.text : 'text-slate-600 dark:text-slate-400'}`}>
                  {phase.name.split(' ')[0]}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {phase.adc_target} ADC
                </p>
                {/* Milestone Status Badge */}
                <div className="mt-1.5">
                  <MilestoneBadge status={milestoneStatus} size="sm" showIcon={false} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Gap Analysis Card
const GapAnalysisCard = ({ gap, title, icon: Icon, color }) => {
  const isPositiveGap = gap.gap_to_phase <= 0;
  
  return (
    <Card className="h-full">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${color}`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <h4 className="font-semibold text-sm">{title}</h4>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Current</span>
            <span className="font-mono font-bold">{gap.current || gap.current_adc || gap.current_rncm || gap.current_monthly_revenue?.toLocaleString() || gap.current_compliance}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Phase Target</span>
            <span className="font-mono">{gap.phase_target?.toLocaleString?.() || gap.phase_target}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-sm text-muted-foreground">Gap</span>
            <Badge className={isPositiveGap ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {isPositiveGap ? '✓ On Target' : gap.gap_to_phase}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Recommendation Card
const RecommendationCard = ({ rec }) => {
  return (
    <Card className="hover:shadow-lg transition-all" data-testid="recommendation-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Badge className={`${priorityColors[rec.priority]} border`}>{rec.priority}</Badge>
          <Badge variant="outline">{rec.category}</Badge>
        </div>
        <CardTitle className="text-base mt-2">{rec.strategic_goal}</CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {rec.actions?.map((action, idx) => (
            <AccordionItem key={idx} value={`action-${idx}`}>
              <AccordionTrigger className="text-sm font-medium hover:no-underline">
                {action.action}
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <div>
                    <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Tactics</h5>
                    <ul className="space-y-1">
                      {action.tactics?.map((tactic, tidx) => (
                        <li key={tidx} className="flex items-start gap-2 text-sm">
                          <ChevronRight className="h-4 w-4 text-[#00b0ab] mt-0.5 flex-shrink-0" />
                          {tactic}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {action.timeline}
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {action.expected_impact}
                    </Badge>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        
        {rec.kpi_targets && (
          <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-2">KPI Targets</h5>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(rec.kpi_targets).slice(0, 3).map(([key, value]) => (
                <div key={key} className="text-center">
                  <p className="text-xs text-muted-foreground">{key.replace(/_/g, ' ')}</p>
                  <p className="font-mono font-bold text-sm">{typeof value === 'number' && value < 1 ? `${(value * 100).toFixed(0)}%` : value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {rec.risk_if_not_addressed && (
          <div className="mt-3 flex items-start gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-400">{rec.risk_if_not_addressed}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Risk Card
const RiskCard = ({ risk }) => {
  const severityColors = {
    HIGH: 'bg-red-500',
    MEDIUM: 'bg-amber-500',
    LOW: 'bg-blue-500'
  };
  
  return (
    <Card className="border-l-4" style={{ borderLeftColor: risk.severity === 'HIGH' ? '#EF4444' : risk.severity === 'MEDIUM' ? '#F59E0B' : '#3B82F6' }}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <Badge variant="outline">{risk.category}</Badge>
          <div className="flex gap-1">
            <Badge className={`${severityColors[risk.severity]} text-white text-xs`}>{risk.severity}</Badge>
            <Badge variant="outline" className="text-xs">{risk.likelihood}</Badge>
          </div>
        </div>
        <p className="font-medium text-sm mb-2">{risk.risk}</p>
        <div className="space-y-2">
          <div>
            <p className="text-xs text-muted-foreground">Mitigation:</p>
            <p className="text-sm">{risk.mitigation}</p>
          </div>
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">Impact if realized:</p>
            <p className="text-sm text-red-600 dark:text-red-400">{risk.impact_if_realized}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Component
export default function StrategicPlanDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [scorecardData, setScorecardData] = useState(null);
  const [milestoneAlerts, setMilestoneAlerts] = useState([]);
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [aiStrategistData, setAiStrategistData] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Generate milestone alerts based on strategic data
  const generateMilestoneAlerts = useCallback((data) => {
    if (!data) return [];
    
    const alerts = [];
    const { current_performance, gap_analysis, trajectory } = data;
    const currentAdc = current_performance?.adc || 50;
    const currentPhase = current_performance?.phase || 'phase_1';
    const milestone = MILESTONES[currentPhase];
    
    if (!milestone) return alerts;
    
    const progressToPhase = (currentAdc / milestone.adc_target) * 100;
    
    // Approaching milestone (90-99%)
    if (progressToPhase >= 90 && progressToPhase < 100) {
      alerts.push({
        id: `approaching-${currentPhase}`,
        type: 'milestone_approaching',
        title: `Approaching ${milestone.name}!`,
        message: `Only ${milestone.adc_target - currentAdc} ADC away from achieving this milestone!`,
        priority: 'high',
        icon: Zap,
        color: 'amber'
      });
    }
    
    // Milestone achieved
    if (currentAdc >= milestone.adc_target) {
      alerts.push({
        id: `achieved-${currentPhase}`,
        type: 'milestone_achieved',
        title: `${milestone.name} Achieved!`,
        message: `Congratulations! You've reached ${milestone.adc_target} ADC.`,
        priority: 'high',
        icon: Trophy,
        color: 'emerald'
      });
    }
    
    // Trajectory at risk
    if (trajectory?.on_track === false) {
      alerts.push({
        id: 'trajectory-risk',
        type: 'at_risk',
        title: 'Growth Trajectory Alert',
        message: `Current growth (${trajectory.current_net_growth}/week) below required (${trajectory.required_weekly_growth}/week)`,
        priority: 'high',
        icon: AlertTriangle,
        color: 'red'
      });
    } else if (trajectory?.assessment === 'Exceeding Plan') {
      alerts.push({
        id: 'trajectory-exceeding',
        type: 'positive',
        title: 'Exceeding Growth Targets!',
        message: `Net growth of ${trajectory.current_net_growth}/week exceeds requirements!`,
        priority: 'medium',
        icon: Rocket,
        color: 'emerald'
      });
    }
    
    // Workforce gap
    if (gap_analysis?.workforce?.gap_to_phase > 3) {
      alerts.push({
        id: 'workforce-gap',
        type: 'warning',
        title: 'Staffing Gap Detected',
        message: `Need ${gap_analysis.workforce.gap_to_phase} more RNCMs to support growth`,
        priority: 'medium',
        icon: Users,
        color: 'amber'
      });
    }
    
    return alerts;
  }, []);

  // Fetch strategic overview
  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [overviewRes, scorecardRes] = await Promise.all([
        fetch(`${API_URL}/api/strategic-plan/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/strategic-plan/scorecard`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (overviewRes.ok && scorecardRes.ok) {
        const [overview, scorecard] = await Promise.all([
          overviewRes.json(),
          scorecardRes.json()
        ]);
        setOverviewData(overview);
        setScorecardData(scorecard);
        
        // Generate and show milestone alerts
        const alerts = generateMilestoneAlerts(overview);
        setMilestoneAlerts(alerts);
        
        // Show toast notifications for high priority alerts on first load
        if (!isRefresh && alerts.length > 0) {
          alerts
            .filter(a => a.priority === 'high')
            .slice(0, 2)
            .forEach((alert, idx) => {
              setTimeout(() => {
                const Icon = alert.icon;
                if (alert.type === 'milestone_achieved' || alert.type === 'positive') {
                  toast.success(alert.title, { 
                    description: alert.message,
                    duration: 6000,
                    icon: <Icon className="h-5 w-5" />
                  });
                } else if (alert.type === 'milestone_approaching') {
                  toast(alert.title, { 
                    description: alert.message,
                    duration: 6000,
                    icon: <Icon className="h-5 w-5 text-amber-500" />
                  });
                } else if (alert.type === 'at_risk') {
                  toast.warning(alert.title, { 
                    description: alert.message,
                    duration: 6000,
                    icon: <Icon className="h-5 w-5" />
                  });
                }
              }, idx * 1500);
            });
        }
        
        if (isRefresh) toast.success('Data refreshed');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error loading strategic plan data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch AI Strategist Executive Report
  const fetchAiStrategistReport = async (forceRefresh = false) => {
    if (!token) return;
    setLoadingAudit(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai-strategist/executive-report?force_refresh=${forceRefresh}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAiStrategistData(data.report);
        if (forceRefresh) {
          toast.success('Executive audit report generated');
        }
      }
    } catch (error) {
      console.error('AI Strategist Error:', error);
      toast.error('Error loading executive audit');
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Load AI Strategist data when tab is activated
  useEffect(() => {
    if (activeTab === 'executive-audit' && !aiStrategistData) {
      fetchAiStrategistReport();
    }
  }, [activeTab, token]);

  // Resource projection chart data
  const resourceChartData = useMemo(() => {
    if (!overviewData?.resource_projections?.phase_projections) return [];
    return overviewData.resource_projections.phase_projections.map(p => ({
      phase: `Year ${p.year}`,
      adc: p.adc,
      rncm: p.rncm_needed,
      revenue: p.estimated_revenue / 1000000
    }));
  }, [overviewData]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="loading">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  const { current_performance, gap_analysis, trajectory, recommendations, risk_assessment } = overviewData || {};
  const alertCount = milestoneAlerts.filter(a => a.priority === 'high').length;

  return (
    <div className="space-y-6" data-testid="strategic-plan-dashboard">
      {/* Header */}
      <PageHeader
        icon={Target}
        title="Strategic Plan Dashboard"
        subtitle="5-Year Growth Plan: Scaling Excellence from 170 to 450 ADC"
        gradient="from-[#00b0ab] via-[#009590] to-[#48494b]"
      >
        <div className="flex items-center gap-2">
          {/* Milestone Alerts Button with Badge */}
          <div className="relative">
            <Button 
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => setShowAlertsPanel(!showAlertsPanel)}
              data-testid="alerts-toggle-btn"
            >
              <Bell className="h-4 w-4 mr-2" />
              Alerts
            </Button>
            {alertCount > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold rounded-full bg-red-500 text-white">
                {alertCount}
              </span>
            )}
          </div>
          
          <Button 
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </PageHeader>

      {/* Milestone Alerts Panel */}
      {showAlertsPanel && milestoneAlerts.length > 0 && (
        <Card className="border-2 border-violet-200 dark:border-violet-800 animate-in slide-in-from-top-2" data-testid="milestone-alerts-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-violet-500" />
              Milestone Alerts
              <Badge variant="secondary" className="ml-2">{milestoneAlerts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {milestoneAlerts.map(alert => {
                const Icon = alert.icon;
                const colorClasses = {
                  emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200',
                  amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200',
                  red: 'bg-red-50 dark:bg-red-900/20 border-red-200',
                  blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200'
                };
                const iconColors = {
                  emerald: 'text-emerald-600',
                  amber: 'text-amber-600',
                  red: 'text-red-600',
                  blue: 'text-blue-600'
                };
                
                return (
                  <div 
                    key={alert.id}
                    className={`p-3 rounded-lg border ${colorClasses[alert.color] || colorClasses.blue}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className={`h-5 w-5 mt-0.5 ${iconColors[alert.color] || iconColors.blue}`} />
                      <div>
                        <h4 className="font-semibold text-sm">{alert.title}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase Progress Badge - Quick Status */}
      {current_performance && (
        <div className="flex items-center justify-between">
          <PhaseProgressBadge 
            currentAdc={current_performance.adc}
            phaseTarget={MILESTONES[current_performance.phase]?.adc_target || 170}
            phaseName={MILESTONES[current_performance.phase]?.name?.split(' ')[0] || 'Foundation'}
          />
          {trajectory?.on_track ? (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <TrendingUp className="h-3 w-3 mr-1" />
              On Track for 5-Year Goal
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {trajectory?.assessment || 'Behind Schedule'}
            </Badge>
          )}
        </div>
      )}

      {/* Vision & Mission */}
      <Card className="bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border-violet-200 dark:border-violet-800">
        <CardContent className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-2">
                <Flag className="h-4 w-4" /> Vision
              </h3>
              <p className="text-sm mt-1">{overviewData?.vision}</p>
            </div>
            <div>
              <h3 className="font-semibold text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
                <Rocket className="h-4 w-4" /> Mission
              </h3>
              <p className="text-sm mt-1">{overviewData?.mission}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ModernCard>
          <CardContent className="p-4 text-center">
            <Gauge className="h-8 w-8 mx-auto text-[#00b0ab] mb-2" />
            <p className="text-sm text-muted-foreground">Overall Progress</p>
            <p className="text-3xl font-bold text-[#00b0ab]">{current_performance?.progress_pct || 0}%</p>
            <Progress value={current_performance?.progress_pct || 0} className="h-2 mt-2" />
          </CardContent>
        </ModernCard>
        
        <ModernCard>
          <CardContent className="p-4 text-center">
            <Building className="h-8 w-8 mx-auto text-[#00b0ab] mb-2" />
            <p className="text-sm text-muted-foreground">Current ADC</p>
            <p className="text-3xl font-bold text-[#00b0ab]">{current_performance?.adc || 50}</p>
            <p className="text-xs text-muted-foreground mt-1">Target: 450</p>
          </CardContent>
        </ModernCard>
        
        <ModernCard>
          <CardContent className="p-4 text-center">
            <Milestone className="h-8 w-8 mx-auto text-amber-600 mb-2" />
            <p className="text-sm text-muted-foreground">Current Phase</p>
            <p className="text-xl font-bold text-amber-600">{current_performance?.phase_name?.split(' ')[0]}</p>
            <Badge className="mt-1">{current_performance?.phase}</Badge>
          </CardContent>
        </ModernCard>
        
        <ModernCard>
          <CardContent className="p-4 text-center">
            {trajectory?.on_track ? (
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
            ) : (
              <AlertCircle className="h-8 w-8 mx-auto text-amber-600 mb-2" />
            )}
            <p className="text-sm text-muted-foreground">Trajectory</p>
            <p className={`text-xl font-bold ${trajectory?.on_track ? 'text-emerald-600' : 'text-amber-600'}`}>
              {trajectory?.assessment}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {trajectory?.years_to_450 ? `${trajectory.years_to_450} yrs to goal` : 'N/A'}
            </p>
          </CardContent>
        </ModernCard>
      </div>

      {/* Phase Timeline */}
      <ModernCard>
        <CardHeader>
          <SectionHeader icon={Calendar} title="5-Year Strategic Phases" subtitle="Progress through each phase milestone" />
        </CardHeader>
        <CardContent>
          <PhaseTimeline 
            phases={overviewData?.phases || []} 
            currentPhase={current_performance?.phase}
            currentAdc={current_performance?.adc}
          />
        </CardContent>
      </ModernCard>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="overview" className={modernTabTriggerClass}>
            <BarChart3 className="h-4 w-4 mr-2" /> Gap Analysis
          </TabsTrigger>
          <TabsTrigger value="insights" className={modernTabTriggerClass}>
            <Lightbulb className="h-4 w-4 mr-2" /> Insights
          </TabsTrigger>
          <TabsTrigger value="predictions" className={modernTabTriggerClass}>
            <TrendingUp className="h-4 w-4 mr-2" /> Predictions
          </TabsTrigger>
          <TabsTrigger value="recommendations" className={modernTabTriggerClass}>
            <Target className="h-4 w-4 mr-2" /> Recommendations
          </TabsTrigger>
          <TabsTrigger value="resources" className={modernTabTriggerClass}>
            <Briefcase className="h-4 w-4 mr-2" /> Resources
          </TabsTrigger>
          <TabsTrigger value="risks" className={modernTabTriggerClass}>
            <Shield className="h-4 w-4 mr-2" /> Risks
          </TabsTrigger>
          <TabsTrigger value="scorecard" className={modernTabTriggerClass}>
            <Award className="h-4 w-4 mr-2" /> Scorecard
          </TabsTrigger>
          <TabsTrigger value="executive-audit" className={modernTabTriggerClass}>
            <Gauge className="h-4 w-4 mr-2" /> Executive Audit
          </TabsTrigger>
          <TabsTrigger value="history" className={modernTabTriggerClass}>
            <Clock className="h-4 w-4 mr-2" /> History
          </TabsTrigger>
        </TabsList>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-6" data-testid="insights-tab">
          <div className="flex items-center justify-between">
            <SectionHeader 
              icon={Lightbulb} 
              title="Strategic Insights" 
              subtitle="Data-driven analysis based on current system metrics"
            />
            <InsightsExport 
              insights={(overviewData?.insights || []).map(i => ({
                title: i.title || i.category,
                description: i.message || i.insight,
                severity: i.type === 'risk' ? 'critical' : i.type === 'growth' || i.type === 'positive' ? 'positive' : 'info',
                category: i.domain || 'Strategic',
                recommendation: i.action || i.recommendation
              }))} 
              pageTitle="Strategic Plan" 
            />
          </div>
          
          {/* Current Metrics Summary Cards */}
          {overviewData?.current_metrics_summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <ModernCard>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#00b0ab]/15 dark:bg-indigo-900/30">
                      <Users className="h-5 w-5 text-[#00b0ab]" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Census</p>
                      <p className="text-2xl font-bold">{overviewData.current_metrics_summary.census?.adc?.toFixed(0) || 0}</p>
                      <p className="text-xs text-muted-foreground">
                        +{overviewData.current_metrics_summary.census?.weekly_admissions || 0} / 
                        -{overviewData.current_metrics_summary.census?.weekly_discharges || 0} weekly
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
              
              <ModernCard>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
                      <Users className="h-5 w-5 text-[#00b0ab]" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Workforce</p>
                      <p className="text-2xl font-bold">{overviewData.current_metrics_summary.workforce?.rncm_count || 0} RNCMs</p>
                      <p className="text-xs text-muted-foreground">
                        {overviewData.current_metrics_summary.workforce?.staff_utilization?.toFixed(1) || 0}% utilization
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
              
              <ModernCard>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Revenue</p>
                      <p className="text-2xl font-bold">
                        ${((overviewData.current_metrics_summary.financial?.monthly_revenue || 0) / 1000000).toFixed(2)}M
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {overviewData.current_metrics_summary.financial?.net_margin?.toFixed(1) || 0}% margin
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
              
              <ModernCard>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                      <Heart className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Quality</p>
                      <p className="text-2xl font-bold">{overviewData.current_metrics_summary.quality?.pain_control?.toFixed(1) || 0}%</p>
                      <p className="text-xs text-muted-foreground">
                        Pain Control | {overviewData.current_metrics_summary.quality?.compliance_rate?.toFixed(1) || 0}% Compliance
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
            </div>
          )}
          
          {/* Insights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {overviewData?.insights?.map((insight, idx) => {
              const colorMap = {
                growth: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200', icon: 'text-emerald-600' },
                positive: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200', icon: 'text-emerald-600' },
                risk: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200', icon: 'text-red-600' },
                recommendation: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200', icon: 'text-blue-600' }
              };
              const colors = colorMap[insight.type] || colorMap.recommendation;
              const IconComponent = insight.type === 'risk' ? AlertTriangle : 
                                   insight.type === 'positive' || insight.type === 'growth' ? TrendingUp : 
                                   Lightbulb;
              
              return (
                <Card 
                  key={insight.id || idx} 
                  className={`${colors.bg} ${colors.border} border`}
                  data-testid={`insight-${insight.domain?.toLowerCase() || idx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <IconComponent className={`h-5 w-5 mt-0.5 ${colors.icon}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{insight.title}</h4>
                          <Badge variant="outline" className="text-xs">{insight.domain}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{insight.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge 
                            className={
                              insight.impact === 'positive' ? 'bg-emerald-100 text-emerald-700' :
                              insight.impact === 'high' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }
                          >
                            Impact: {insight.impact}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {(!overviewData?.insights || overviewData.insights.length === 0) && (
              <Card className="col-span-2">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No insights available. Add data to generate strategic insights.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="space-y-6" data-testid="predictions-tab">
          <SectionHeader 
            icon={TrendingUp} 
            title="Strategic Predictions" 
            subtitle="4-week and 12-week forecasts based on current trends"
          />
          
          {/* Predictions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {overviewData?.predictions?.map((pred, idx) => {
              const isPositive = pred.trend === 'up' || 
                               (pred.metric === 'Burnout Risk (Staff)' && pred.trend === 'down');
              const trendIcon = pred.trend === 'up' ? TrendingUp : 
                               pred.trend === 'down' ? TrendingDown : 
                               Gauge;
              const TrendIcon = trendIcon;
              
              return (
                <ModernCard key={idx} data-testid={`prediction-${pred.metric?.replace(/\s+/g, '-').toLowerCase()}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm">{pred.metric}</h4>
                      <div className="flex items-center gap-1.5">
                        {pred.source === 'census_forecasting_service' && (
                          <Badge 
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 h-5 bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-800"
                            title="AI-powered forecasting with trend analysis"
                          >
                            <Sparkles className="h-3 w-3 mr-0.5" />
                            AI
                          </Badge>
                        )}
                        <Badge 
                          className={
                            pred.confidence >= 85 ? 'bg-emerald-100 text-emerald-700' :
                            pred.confidence >= 75 ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }
                        >
                          {pred.confidence}% conf
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Current</p>
                        <p className="text-2xl font-bold">{pred.current}</p>
                      </div>
                      
                      <TrendIcon 
                        className={`h-6 w-6 ${isPositive ? 'text-emerald-600' : 'text-amber-600'}`} 
                      />
                      
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">4-Week Forecast</p>
                        <p className={`text-2xl font-bold ${isPositive ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {pred.forecast_4w || pred.forecast || pred.required_for_adc || '-'}
                        </p>
                      </div>
                    </div>
                    
                    {pred.target && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Target</span>
                          <span className="font-medium">{pred.target}</span>
                        </div>
                        <Progress 
                          value={Math.min(100, (pred.current / pred.target) * 100)} 
                          className="h-1.5 mt-1"
                        />
                      </div>
                    )}
                    
                    {pred.phase1_target && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Phase 1 Target</span>
                          <span className="font-medium">{pred.phase1_target}</span>
                        </div>
                        <Progress 
                          value={Math.min(100, (pred.current / pred.phase1_target) * 100)} 
                          className="h-1.5 mt-1"
                        />
                      </div>
                    )}
                    
                    {pred.gap !== undefined && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Hiring Gap</span>
                          <span className={`font-medium ${pred.gap > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {pred.gap > 0 ? `+${pred.gap} needed` : 'Adequate'}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </ModernCard>
              );
            })}
            
            {(!overviewData?.predictions || overviewData.predictions.length === 0) && (
              <Card className="col-span-3">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No predictions available. Add data to generate forecasts.</p>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Milestone Progress */}
          {overviewData?.milestones?.length > 0 && (
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Flag} title="Milestone Progress" subtitle="Progress toward next strategic milestone" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {overviewData.milestones.map((milestone, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Flag className={`h-4 w-4 ${milestone.status === 'on_track' ? 'text-emerald-600' : 'text-amber-600'}`} />
                          <span className="font-medium">{milestone.name}</span>
                        </div>
                        <Badge 
                          className={milestone.status === 'on_track' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}
                        >
                          {milestone.status === 'on_track' ? 'On Track' : 'At Risk'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span>Current: <strong>{milestone.current}</strong></span>
                        <span>Target: <strong>{milestone.target}</strong></span>
                        <span>Progress: <strong>{milestone.progress_pct}%</strong></span>
                        {milestone.weeks_to_achieve !== 'N/A' && (
                          <span className="text-muted-foreground">~{milestone.weeks_to_achieve} weeks</span>
                        )}
                      </div>
                      <Progress value={milestone.progress_pct} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </ModernCard>
          )}
        </TabsContent>

        {/* Gap Analysis Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <GapAnalysisCard 
              gap={gap_analysis?.census || {}} 
              title="Census" 
              icon={Building} 
              color="from-indigo-500 to-blue-600"
            />
            <GapAnalysisCard 
              gap={gap_analysis?.workforce || {}} 
              title="Workforce" 
              icon={Users} 
              color="from-violet-500 to-purple-600"
            />
            <GapAnalysisCard 
              gap={gap_analysis?.financial || {}} 
              title="Financial" 
              icon={DollarSign} 
              color="from-emerald-500 to-teal-600"
            />
            <GapAnalysisCard 
              gap={gap_analysis?.quality || {}} 
              title="Quality" 
              icon={Heart} 
              color="from-rose-500 to-pink-600"
            />
          </div>
          
          {/* Overall Gap Assessment */}
          <ModernCard>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">Overall Gap Assessment</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Distance from current phase targets
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">{gap_analysis?.overall_gap_score || 0}%</p>
                  <Badge className={
                    gap_analysis?.assessment === 'On Track' ? 'bg-emerald-100 text-emerald-700' :
                    gap_analysis?.assessment === 'Minor Gap' ? 'bg-blue-100 text-blue-700' :
                    gap_analysis?.assessment === 'Significant Gap' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }>
                    {gap_analysis?.assessment}
                  </Badge>
                </div>
              </div>
              <Progress value={100 - (gap_analysis?.overall_gap_score || 0)} className="h-3 mt-4" />
            </CardContent>
          </ModernCard>

          {/* Trajectory Details */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={TrendingUp} title="Growth Trajectory" subtitle="Projected path to 450 ADC goal" />
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Current Net Growth</p>
                  <p className={`text-3xl font-bold ${(trajectory?.current_net_growth || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {trajectory?.current_net_growth >= 0 ? '+' : ''}{trajectory?.current_net_growth}/week
                  </p>
                </div>
                <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Required Growth</p>
                  <p className="text-3xl font-bold text-[#00b0ab]">+{trajectory?.required_weekly_growth}/week</p>
                </div>
                <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Growth Deficit</p>
                  <p className={`text-3xl font-bold ${(trajectory?.growth_deficit || 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {trajectory?.growth_deficit > 0 ? trajectory.growth_deficit : 'None'}
                  </p>
                </div>
              </div>
              
              {trajectory?.milestone_projections?.length > 0 && (
                <div className="mt-6">
                  <h5 className="font-semibold text-sm mb-3">Milestone Projections</h5>
                  <div className="space-y-2">
                    {trajectory.milestone_projections.map((m, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{m.phase}</Badge>
                          <span className="font-medium">{m.target_adc} ADC</span>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm">{m.weeks_to_reach} weeks</p>
                          <p className="text-xs text-muted-foreground">{m.projected_date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(recommendations || []).map((rec, idx) => (
              <RecommendationCard key={idx} rec={rec} />
            ))}
          </div>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="space-y-6">
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={BarChart3} title="Resource Projections by Phase" />
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={resourceChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="phase" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.75rem'
                    }} 
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="adc" name="ADC" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="rncm" name="RNCMs Needed" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="revenue" name="Revenue ($M)" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </ModernCard>

          {/* Investment Summary */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={DollarSign} title="5-Year Investment Summary" />
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {Object.entries(overviewData?.resource_projections?.investment_summary || {}).map(([key, value]) => (
                  <div key={key} className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                    <p className="text-2xl font-bold text-emerald-600">${(value / 1000000).toFixed(2)}M</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Risks Tab */}
        <TabsContent value="risks" className="space-y-6">
          <ModernCard>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">Overall Risk Level</h4>
                  <p className="text-sm text-muted-foreground">Based on gap analysis and strategic assessment</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">{risk_assessment?.overall_risk_score || 0}%</p>
                  <Badge className={
                    risk_assessment?.risk_level === 'LOW' ? 'bg-emerald-100 text-emerald-700' :
                    risk_assessment?.risk_level === 'MODERATE' ? 'bg-blue-100 text-blue-700' :
                    risk_assessment?.risk_level === 'ELEVATED' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }>
                    {risk_assessment?.risk_level}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </ModernCard>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(risk_assessment?.risks || []).map((risk, idx) => (
              <RiskCard key={idx} risk={risk} />
            ))}
          </div>
        </TabsContent>

        {/* Scorecard Tab */}
        <TabsContent value="scorecard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Census Scorecard */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Building} title="Census Scorecard" />
              </CardHeader>
              <CardContent className="space-y-4">
                {scorecardData?.census && Object.entries(scorecardData.census).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">Target: {data.target}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{typeof data.current === 'number' && data.current < 1 ? `${(data.current * 100).toFixed(1)}%` : data.current}</p>
                      {data.progress_pct !== undefined && (
                        <Progress value={data.progress_pct} className="h-1.5 w-20 mt-1" />
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </ModernCard>

            {/* Workforce Scorecard */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Users} title="Workforce Scorecard" />
              </CardHeader>
              <CardContent className="space-y-4">
                {scorecardData?.workforce && Object.entries(scorecardData.workforce).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">Target: {data.target}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{data.current}</p>
                      {data.gap !== undefined && data.gap > 0 && (
                        <Badge variant="outline" className="text-xs">Gap: {data.gap}</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </ModernCard>

            {/* Financial Scorecard */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={DollarSign} title="Financial Scorecard" />
              </CardHeader>
              <CardContent className="space-y-4">
                {scorecardData?.financial && Object.entries(scorecardData.financial).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">Target: ${(data.target / 1000000).toFixed(1)}M</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">${(data.current / 1000000).toFixed(2)}M</p>
                      {data.progress_pct !== undefined && (
                        <Progress value={Math.min(100, data.progress_pct)} className="h-1.5 w-20 mt-1" />
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </ModernCard>

            {/* Quality Scorecard */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Heart} title="Quality Scorecard" />
              </CardHeader>
              <CardContent className="space-y-4">
                {scorecardData?.quality && Object.entries(scorecardData.quality).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">Target: {data.target}%</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{data.current}%</p>
                      <Badge className={data.status === 'excellent' || data.status === 'good' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {data.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </ModernCard>
          </div>
        </TabsContent>

        {/* Executive Audit Tab - AI Strategist */}
        <TabsContent value="executive-audit" className="space-y-6" data-testid="executive-audit-tab">
          <div className="flex items-center justify-between">
            <SectionHeader 
              icon={Gauge} 
              title="AI Executive Performance Audit" 
              subtitle="8-Pillar strategic analysis with predictive trajectory"
            />
            <Button 
              onClick={() => fetchAiStrategistReport(true)} 
              disabled={loadingAudit}
              variant="outline"
            >
              {loadingAudit ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Generate New Report
                </>
              )}
            </Button>
          </div>

          {loadingAudit && !aiStrategistData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
            </div>
          ) : aiStrategistData ? (
            <>
              {/* Executive Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Organizational Health Score */}
                <ModernCard className="relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-1 h-full ${
                    aiStrategistData.organizational_health?.color === 'green' ? 'bg-emerald-500' :
                    aiStrategistData.organizational_health?.color === 'yellow' ? 'bg-amber-500' :
                    aiStrategistData.organizational_health?.color === 'orange' ? 'bg-orange-500' : 'bg-red-500'
                  }`} />
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Health Score</span>
                      <Badge className={
                        aiStrategistData.organizational_health?.grade === 'A' ? 'bg-emerald-100 text-emerald-700' :
                        aiStrategistData.organizational_health?.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        Grade {aiStrategistData.organizational_health?.grade}
                      </Badge>
                    </div>
                    <p className="text-3xl font-bold">{aiStrategistData.organizational_health?.composite_score?.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{aiStrategistData.organizational_health?.category}</p>
                  </CardContent>
                </ModernCard>

                {/* Strategic Phase */}
                <ModernCard>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Strategic Phase</span>
                      <Badge className={
                        aiStrategistData.strategic_phase?.alignment_status === 'On Track' || 
                        aiStrategistData.strategic_phase?.alignment_status === 'Ahead of Plan' 
                          ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }>
                        {aiStrategistData.strategic_phase?.alignment_status}
                      </Badge>
                    </div>
                    <p className="text-xl font-bold">{aiStrategistData.strategic_phase?.current_phase?.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Year {aiStrategistData.strategic_phase?.current_phase?.year} of 5-Year Plan
                    </p>
                  </CardContent>
                </ModernCard>

                {/* Year 5 Probability */}
                <ModernCard>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">450 ADC by 2030</span>
                      <Target className="h-5 w-5 text-violet-500" />
                    </div>
                    <p className={`text-2xl font-bold ${
                      aiStrategistData.trajectory?.year_5_projection?.probability === 'High' ? 'text-emerald-600' :
                      aiStrategistData.trajectory?.year_5_projection?.probability === 'Moderate' ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {aiStrategistData.trajectory?.year_5_projection?.probability} Probability
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Projected: {aiStrategistData.trajectory?.year_5_projection?.projected_adc} ADC
                    </p>
                  </CardContent>
                </ModernCard>

                {/* Primary Constraint */}
                <ModernCard>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Primary Constraint</span>
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                    </div>
                    <p className="text-lg font-bold">{aiStrategistData.trajectory?.primary_constraint?.area}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {aiStrategistData.trajectory?.primary_constraint?.issue}
                    </p>
                  </CardContent>
                </ModernCard>
              </div>

              {/* 8-Pillar Strategic Grades */}
              <ModernCard>
                <CardHeader>
                  <SectionHeader icon={Award} title="8 Strategic Pillar Grades" />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {aiStrategistData.pillar_grades && Object.entries(aiStrategistData.pillar_grades).map(([key, pillar]) => (
                      <div key={key} className={`p-4 rounded-lg border ${
                        pillar.grade === 'A' ? 'border-emerald-200 bg-emerald-50/50' :
                        pillar.grade === 'B' ? 'border-blue-200 bg-blue-50/50' :
                        pillar.grade === 'C' ? 'border-amber-200 bg-amber-50/50' :
                        pillar.grade === 'D' ? 'border-orange-200 bg-orange-50/50' :
                        'border-red-200 bg-red-50/50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-2xl font-bold ${
                            pillar.grade === 'A' ? 'text-emerald-600' :
                            pillar.grade === 'B' ? 'text-blue-600' :
                            pillar.grade === 'C' ? 'text-amber-600' :
                            pillar.grade === 'D' ? 'text-orange-600' : 'text-red-600'
                          }`}>{pillar.grade}</span>
                          <span className="text-sm text-muted-foreground">{pillar.score}/100</span>
                        </div>
                        <p className="text-sm font-medium mb-2">{pillar.pillar}</p>
                        {pillar.issues?.length > 0 && (
                          <div className="space-y-1">
                            {pillar.issues.slice(0, 2).map((issue, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  issue.severity === 'HIGH' ? 'bg-red-500' :
                                  issue.severity === 'MEDIUM' ? 'bg-amber-500' : 'bg-blue-500'
                                }`} />
                                <span className="text-xs text-muted-foreground truncate">{issue.flag}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </ModernCard>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Risk Indices */}
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Shield} title="Risk Indices" />
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {aiStrategistData.risk_indices && Object.entries(aiStrategistData.risk_indices).map(([key, risk]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className={`text-lg font-bold ${
                            risk.level === 'Low' || risk.level === 'Strong' ? 'text-emerald-600' :
                            risk.level === 'Moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>{risk.level}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">{risk.score}</div>
                          <Progress 
                            value={risk.score} 
                            className={`h-2 w-20 ${
                              risk.level === 'Low' || risk.level === 'Strong' ? '[&>div]:bg-emerald-500' :
                              risk.level === 'Moderate' ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'
                            }`} 
                          />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </ModernCard>

                {/* Executive Priorities */}
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Target} title="Executive Priorities This Quarter" />
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {aiStrategistData.executive_priorities?.map((priority, idx) => (
                      <div key={idx} className="p-4 border rounded-lg bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-800/30">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-amber-500' : 'bg-blue-500'
                          }`}>{priority.priority}</span>
                          <span className="font-semibold">{priority.focus}</span>
                        </div>
                        <p className="text-sm text-muted-foreground ml-11">{priority.rationale}</p>
                        <p className="text-sm mt-2 ml-11 text-[#00b0ab] dark:text-violet-400">
                          <ArrowRight className="inline h-4 w-4 mr-1" />
                          {priority.action}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </ModernCard>
              </div>

              {/* Trajectory Forecasts */}
              <ModernCard>
                <CardHeader>
                  <SectionHeader icon={TrendingUp} title="Predictive Trajectory" />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">Current ADC</p>
                      <p className="text-3xl font-bold">{aiStrategistData.trajectory?.current_adc}</p>
                    </div>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">6-Month Forecast</p>
                      <p className="text-3xl font-bold text-blue-600">{aiStrategistData.trajectory?.forecast_6_months?.adc}</p>
                      <p className="text-xs text-muted-foreground">{aiStrategistData.trajectory?.forecast_6_months?.date}</p>
                    </div>
                    <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">12-Month Forecast</p>
                      <p className="text-3xl font-bold text-[#00b0ab]">{aiStrategistData.trajectory?.forecast_12_months?.adc}</p>
                      <p className="text-xs text-muted-foreground">{aiStrategistData.trajectory?.forecast_12_months?.date}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">2030 Target</p>
                      <p className="text-3xl font-bold text-emerald-600">{aiStrategistData.trajectory?.year_5_projection?.target_adc}</p>
                      <p className="text-xs text-muted-foreground">Projected: {aiStrategistData.trajectory?.year_5_projection?.projected_adc}</p>
                    </div>
                  </div>
                  
                  {/* Financial Trajectory */}
                  <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                      Financial Trajectory to $70-90M
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Current Annual</p>
                        <p className="text-xl font-bold">{aiStrategistData.trajectory?.financial_trajectory?.current_annual_revenue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Projected 2030</p>
                        <p className="text-xl font-bold text-emerald-600">{aiStrategistData.trajectory?.financial_trajectory?.projected_2030_revenue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Target</p>
                        <p className="text-xl font-bold">{aiStrategistData.trajectory?.financial_trajectory?.target_revenue}</p>
                        <Badge className={aiStrategistData.trajectory?.financial_trajectory?.on_track ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {aiStrategistData.trajectory?.financial_trajectory?.on_track ? 'On Track' : 'At Risk'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>

              {/* Acceleration Plan */}
              {aiStrategistData.acceleration_plan && (
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Rocket} title="Acceleration Plan to 450 ADC" />
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3">Staffing Model</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                            <span>Current RNCMs</span>
                            <span className="font-bold">{aiStrategistData.acceleration_plan.structural_upgrades?.staffing_model?.current_rncm}</span>
                          </div>
                          <div className="flex justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                            <span>Required RNCMs</span>
                            <span className="font-bold">{aiStrategistData.acceleration_plan.structural_upgrades?.staffing_model?.required_rncm}</span>
                          </div>
                          <div className="flex justify-between p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                            <span>Additional Hires Needed</span>
                            <span className="font-bold text-emerald-600">{aiStrategistData.acceleration_plan.structural_upgrades?.staffing_model?.additional_hires}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">Capital Requirements</h4>
                        <div className="space-y-2 text-sm">
                          {aiStrategistData.acceleration_plan.structural_upgrades?.capital_requirements && 
                            Object.entries(aiStrategistData.acceleration_plan.structural_upgrades.capital_requirements).map(([key, value]) => (
                              <div key={key} className="flex justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                                <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                                <span className="font-bold">{value}</span>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3">Centralization Needs</h4>
                        <ul className="space-y-2">
                          {aiStrategistData.acceleration_plan.structural_upgrades?.centralization_needs?.map((need, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              {need}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">AI Integration Opportunities</h4>
                        <ul className="space-y-2">
                          {aiStrategistData.acceleration_plan.structural_upgrades?.ai_integration?.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm">
                              <Zap className="h-4 w-4 text-violet-500" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </ModernCard>
              )}

              {/* Report Timestamp */}
              <p className="text-xs text-muted-foreground text-center">
                Report generated: {new Date(aiStrategistData.report_generated_at).toLocaleString()}
              </p>
            </>
          ) : (
            <div className="text-center py-12">
              <Gauge className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Click "Generate New Report" to run the AI Executive Audit</p>
            </div>
          )}
        </TabsContent>

        {/* Data History Tab */}
        <TabsContent value="history" className="space-y-6">
          <SectionHeader 
            icon={Clock} 
            title="Strategic Data History" 
            subtitle="View timeline of data entries related to strategic planning"
          />
          <div className="grid gap-4">
            <DataHistoryPanel 
              category="census" 
              title="Census & Growth Data" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="program" 
              title="Program Overview" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="financial" 
              title="Financial Metrics" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="workforce" 
              title="Workforce Data" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
